class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    if (facetForm) facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static normalizeSearchParams(searchParams = '') {
    const params = new URLSearchParams(searchParams);

    ['filter.v.price.gte', 'filter.v.price.lte', 'sleeper_category', 'sleeper_color_group'].forEach((key) => {
      const value = params.get(key);
      if (value !== null && String(value).trim() === '') params.delete(key);
    });

    const hasVariantFilter = Array.from(params.keys()).some((key) => {
      const normalizedKey = String(key).toLowerCase();
      return normalizedKey.includes('filter.v.option.size') || normalizedKey.includes('filter.v.option.color');
    });

    if (hasVariantFilter) params.set('filter.v.availability', '1');
    else params.delete('filter.v.availability');

    return params.toString();
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    searchParams = FacetFiltersForm.normalizeSearchParams(searchParams);
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const collection = document.getElementById('ProductGridContainer')?.querySelector('.collection');
    if (collection) collection.classList.add('loading');
    if (countContainer) countContainer.classList.add('loading');
    if (countContainerDesktop) countContainerDesktop.classList.add('loading');

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

    sections.forEach((section) => {
      const url = window.location.pathname + '?section_id=' + section.section + '&' + searchParams;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.handleCustomBlocksInGrid();
        SleeperFacetUI.polish();
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.handleCustomBlocksInGrid();
    SleeperFacetUI.polish();
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
  }

  static handleCustomBlocksInGrid() {
    const urlParams = new URLSearchParams(window.location.search);
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    const hasMerchandisingFilter = Array.from(urlParams.keys()).some((key) => {
      const normalizedKey = String(key).toLowerCase();
      return normalizedKey.includes('filter.v.option.size') ||
        normalizedKey.includes('filter.v.option.color') ||
        normalizedKey.includes('filter.p.m.custom.material') ||
        normalizedKey.includes('filter.v.price.gte') ||
        normalizedKey.includes('filter.v.price.lte') ||
        normalizedKey === 'sleeper_category' ||
        normalizedKey === 'sleeper_color_group';
    });

    container.querySelectorAll('.product-grid__custom-block').forEach((element) => {
      element.classList.toggle('hidden', hasMerchandisingFilter);
    });
  }

  static renderProductGridContainer(html) {
    const target = document.getElementById('ProductGridContainer');
    const source = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');
    if (!target || !source) return;
    target.innerHTML = source.innerHTML;
    if (typeof window.SleeperApplyAllCollectionFilters === 'function') {
      window.SleeperApplyAllCollectionFilters();
    } else if (typeof window.SleeperApplyAvailableVariantFilter === 'function') {
      window.SleeperApplyAvailableVariantFilter();
    }
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsed.getElementById('ProductCount');
    if (!sourceCount) return;
    const count = sourceCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.querySelector('.js-filter[data-index="' + element.dataset.index + '"]');
      if (target) target.innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    if (countsToRender && event) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static renderActiveFacets(html) {
    ['.active-facets-mobile', '.active-facets-desktop'].forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (source && target) target.innerHTML = source.innerHTML;
    });
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    ['.mobile-facets__open', '.mobile-facets__count', '.sorting'].forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (source && target) target.innerHTML = source.innerHTML;
    });
    SleeperFacetUI.polish();
    if (typeof window.SleeperSyncColorGroups === 'function') window.SleeperSyncColorGroups(document);
    if (typeof window.SleeperSyncSizeGroups === 'function') window.SleeperSyncSizeGroups(document);
  }

  static renderCounts(source, target) {
    if (!source || !target) return;
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');
    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');
    if (sourceElement && targetElement) targetElement.outerHTML = sourceElement.outerHTML;
    if (targetElementAccessibility && sourceElementAccessibility) {
      targetElementAccessibility.outerHTML = sourceElementAccessibility.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', window.location.pathname + (searchParams ? '?' + searchParams : ''));
  }

  static getSections() {
    const grid = document.getElementById('product-grid');
    return grid ? [{ section: grid.dataset.id }] : [];
  }

  createSearchParams(form) {
    return new URLSearchParams(new FormData(form)).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const form = event.target.closest('form');
    if (!form) return;

    if (form.id === 'FacetFiltersFormMobile') return;

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const forms = [];
    sortFilterForms.forEach((currentForm) => {
      if (
        currentForm.id === 'FacetSortForm' ||
        currentForm.id === 'FacetFiltersForm' ||
        currentForm.id === 'FacetSortDrawerForm'
      ) {
        document.querySelectorAll('.no-js-list').forEach((el) => el.remove());
        forms.push(this.createSearchParams(currentForm));
      }
    });
    this.onSubmitForm(forms.join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const href = event.currentTarget.href;
    const url = href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url, event);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = FacetFiltersForm.normalizeSearchParams(window.location.search.slice(1));
FacetFiltersForm.searchParamsPrev = FacetFiltersForm.searchParamsInitial;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (!minInput || !maxInput) return;
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));
    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}
customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (!facetLink) return;
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (form) form.onActiveFilterClick(event);
  }
}
customElements.define('facet-remove', FacetRemove);

const SleeperFacetUI = {
  styleId: 'sleeper-facets-v3-style',

  ensureStyle() {
    if (document.getElementById(this.styleId)) return;
    const style = document.createElement('style');
    style.id = this.styleId;
    style.textContent = [
      '@media screen and (max-width: 749px) {',
      'body .sleeper-filter-drawer .mobile-facets__inner { overflow: hidden !important; }',
      'body .sleeper-filter-drawer .sleeper-facets-main { flex: 1 1 auto !important; min-height: 0 !important; overflow-y: auto !important; padding-bottom: 0 !important; }',
      'body .sleeper-filter-drawer .sleeper-facets-footer { position: static !important; inset: auto !important; left: auto !important; right: auto !important; bottom: auto !important; flex: 0 0 auto !important; margin: 0 !important; z-index: 20 !important; }',
      '}'
    ].join('\n');
    document.head.appendChild(style);
  },

  polish() {
    this.ensureStyle();
    const form = document.getElementById('FacetFiltersFormMobile');
    if (!form) return;

    const inner = form.querySelector('.mobile-facets__inner');
    const main = form.querySelector('.sleeper-facets-main');
    const footer = form.querySelector('.sleeper-facets-footer');

    if (inner && main && footer && footer.parentElement === main) {
      main.insertAdjacentElement('afterend', footer);
    }
  }
};

(() => {
  document.addEventListener(
    'click',
    (event) => {
      const clearLink = event.target.closest('[data-sleeper-clear-filters]');
      if (!clearLink) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      window.location.assign(window.location.pathname);
    },
    true
  );

  const productCache = new Map();
  const normalize = (value) => String(value || '').trim().toLowerCase();

  const CATEGORY_TAGS = {
    dresses: ['dresses', 'dress'],
    pajamas: ['pajamas', 'sets'],
    sets: ['sets'],
    tops: ['tops', 'top', 'shirts', 'shirt'],
    bottoms: ['pants', 'shorts'],
    outerwear: ['outerwear', 'jacket', 'jackets', 'knitwear', 'cardigans', 'cardigan', 'poncho'],
    shoes: ['shoes', 'accessories']
  };

  const selectedFacetValues = (facetName) => {
    const params = new URLSearchParams(window.location.search);
    const values = [];
    for (const [key, value] of params.entries()) {
      const normalizedKey = normalize(key);
      if (normalizedKey.includes('filter.v.option.' + facetName)) values.push(normalize(value));
    }
    return values.filter(Boolean);
  };

  const findOptionIndex = (options, names) => {
    return (options || []).findIndex((option) => {
      const optionName =
        typeof option === 'string'
          ? option
          : option && typeof option === 'object'
            ? option.name
            : '';
      const normalizedName = normalize(optionName);
      return names.some((candidate) => normalizedName === candidate || normalizedName.includes(candidate));
    });
  };

  const getHandle = (item) => {
    const discountNinjaLink = item.querySelector('[data-limoniapps-discountninja-product-handle]');
    let handle = discountNinjaLink?.dataset?.limoniappsDiscountninjaProductHandle || '';
    if (!handle) {
      const productLink = item.querySelector('a[href*="/products/"]');
      const href = productLink?.getAttribute('href') || '';
      const match = href.match(/\/products\/([^/?#]+)/);
      handle = match ? match[1] : '';
    }
    return handle;
  };

  const loadProduct = async (handle) => {
    if (!handle) return null;
    if (!productCache.has(handle)) {
      productCache.set(
        handle,
        fetch('/products/' + encodeURIComponent(handle) + '.js')
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      );
    }
    return productCache.get(handle);
  };

  const productCards = () =>
    Array.from(
      document.querySelectorAll('#product-grid > .grid__item:not(.product-grid__custom-block):not(.grid__item--split)')
    );

  const applyAvailableVariantFilter = async () => {
    const sizes = selectedFacetValues('size');
    const colors = selectedFacetValues('color');
    const cards = productCards();

    if (!sizes.length && !colors.length) {
      cards.forEach((item) => {
        item.classList.remove('sleeper-filtered-out');
      });
      return;
    }

    await Promise.all(
      cards.map(async (item) => {
        const handle = getHandle(item);
        if (!handle) return;

        const product = await loadProduct(handle);
        if (!product?.variants?.length) return;

        const sizeIndex = findOptionIndex(product.options, ['size', 'розмір']);
        const colorIndex = findOptionIndex(product.options, ['color', 'colour', 'колір']);

        const matchesAvailableVariant = product.variants.some((variant) => {
          if (variant.available !== true) return false;
          const values = [variant.option1, variant.option2, variant.option3].map(normalize);
          const sizeMatches = sizes.length === 0 || (sizeIndex >= 0 && sizes.includes(values[sizeIndex]));
          const colorMatches = colors.length === 0 || (colorIndex >= 0 && colors.includes(values[colorIndex]));
          return sizeMatches && colorMatches;
        });

        item.classList.toggle('sleeper-filtered-out', !matchesAvailableVariant);
        if (!matchesAvailableVariant) item.style.setProperty('display', 'none', 'important');
      })
    );
  };

  const syncCategoryUI = () => {
    const params = new URLSearchParams(window.location.search);
    const category = normalize(params.get('sleeper_category'));
    const form = document.getElementById('FacetFiltersFormMobile');
    const hidden = form?.querySelector('[data-sleeper-category-input]');
    if (hidden) hidden.value = category;

    document.querySelectorAll('[data-sleeper-category]').forEach((link) => {
      link.classList.toggle('is-active', normalize(link.dataset.sleeperCategory) === category && category !== '');
    });
  };

  const readPriceBound = (suffix) => {
    const params = new URLSearchParams(window.location.search);
    for (const [key, value] of params.entries()) {
      const normalizedKey = normalize(key);
      if (normalizedKey === 'filter.v.price.' + suffix || normalizedKey.endsWith('price.' + suffix)) {
        const rawValue = String(value ?? '').trim();
        if (rawValue === '') return null;
        const parsed = Number(rawValue.replace(',', '.'));
        if (Number.isFinite(parsed)) return parsed;
      }
    }
    return null;
  };

  const applyClientCollectionFilters = () => {
    syncCategoryUI();

    const params = new URLSearchParams(window.location.search);
    const category = normalize(params.get('sleeper_category'));
    const minPrice = readPriceBound('gte');
    const maxPrice = readPriceBound('lte');
    const allowedTags = CATEGORY_TAGS[category] || [];
    let visibleCount = 0;

    productCards().forEach((item) => {
      const tags = String(item.dataset.sleeperTags || '')
        .split('|')
        .map(normalize)
        .filter(Boolean);

      const categoryMatch =
        !category ||
        allowedTags.length === 0 ||
        allowedTags.some((tag) => tags.includes(tag));

      const priceValue = Number(String(item.dataset.sleeperPriceMin || '').replace(',', '.'));
      const priceMatch =
        !Number.isFinite(priceValue) ||
        ((minPrice === null || priceValue >= minPrice) &&
          (maxPrice === null || priceValue <= maxPrice));

      const clientHidden = !categoryMatch || !priceMatch;
      item.classList.toggle('sleeper-client-filtered-out', clientHidden);

      const availabilityHidden = item.classList.contains('sleeper-filtered-out');
      const shouldHide = availabilityHidden || clientHidden;
      item.hidden = shouldHide;
      if (shouldHide) {
        item.style.setProperty('display', 'none', 'important');
      } else {
        item.style.removeProperty('display');
        visibleCount += 1;
      }
    });

    ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        el.textContent = visibleCount;
        el.classList.remove('loading');
      }
    });
  };

  const applyAllCollectionFilters = async () => {
    if (typeof window.SleeperSyncColorGroups === 'function') {
      window.SleeperSyncColorGroups(document);
    }
    if (typeof window.SleeperSyncSizeGroups === 'function') {
      window.SleeperSyncSizeGroups(document);
    }
    await applyAvailableVariantFilter();
    applyClientCollectionFilters();
    window.setTimeout(applyClientCollectionFilters, 80);
    window.setTimeout(applyClientCollectionFilters, 250);
  };

  window.SleeperApplyAvailableVariantFilter = applyAvailableVariantFilter;
  window.SleeperApplyCollectionClientFilters = applyClientCollectionFilters;
  window.SleeperApplyAllCollectionFilters = applyAllCollectionFilters;

  document.addEventListener(
    'click',
    (event) => {
      const categoryLink = event.target.closest('[data-sleeper-category]');
      if (categoryLink) {
        const form = categoryLink.closest('#FacetFiltersFormMobile');
        if (!form) return;

        event.preventDefault();
        event.stopPropagation();

        const requested = normalize(categoryLink.dataset.sleeperCategory);
        const params = new URLSearchParams(window.location.search);
        const current = normalize(params.get('sleeper_category'));

        if (requested && current === requested) params.delete('sleeper_category');
        else if (requested) params.set('sleeper_category', requested);
        else params.delete('sleeper_category');

        const searchParams = params.toString();
        history.pushState({ searchParams }, '', window.location.pathname + (searchParams ? '?' + searchParams : ''));
        FacetFiltersForm.searchParamsPrev = searchParams;

        syncCategoryUI();
        applyClientCollectionFilters();
        return;
      }

      const button = event.target.closest('.sleeper-facets-footer > button[type="button"]');
      if (!button) return;

      const form = button.closest('#FacetFiltersFormMobile');
      if (!form) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const searchParams = new URLSearchParams(new FormData(form)).toString();
      FacetFiltersForm.renderPage(searchParams, null);

      const disclosure = button.closest('.mobile-facets__disclosure');
      if (disclosure) {
        const summary = disclosure.querySelector(':scope > summary');
        if (summary) summary.click();
        else disclosure.removeAttribute('open');
      }
    },
    true
  );

  const initialize = () => {
    SleeperFacetUI.polish();
    syncCategoryUI();
    if (typeof window.SleeperSyncColorGroups === 'function') window.SleeperSyncColorGroups(document);
    if (typeof window.SleeperSyncSizeGroups === 'function') window.SleeperSyncSizeGroups(document);
    applyAllCollectionFilters();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
